/**
 * auto-gift.js
 * Automatically adds a free gift line item to the cart whenever a specific
 * trigger product is present, and removes the gift again once the trigger
 * product is gone.
 */
(function () {
  'use strict';

  // ---- Configuration ----------------------------------------------------
  var TRIGGER_PRODUCT_IDS = [
    10612145946888, // Raumgarten Pro
    10612146077960  // Raumgarten
  ];

  var GIFT_VARIANT_ID = 45604958732552; // 1 Monat Paket Plugs & Microgreens - 64 Stk - div. Sorten
  var GIFT_QUANTITY = 1;
  var DEBUG = false;

  // ---- Internal state -----------------------------------------------------
  var isSyncing = false;
  var pendingSync = false;
  var debounceTimer = null;

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[auto-gift]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // ---- Cart API helpers ---------------------------------------------------
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('GET /cart.js failed: ' + r.status);
      return r.json();
    });
  }

  function cartHasTrigger(cart) {
    return cart.items.some(function (item) {
      return TRIGGER_PRODUCT_IDS.indexOf(item.product_id) !== -1;
    });
  }

  function findGiftLineItem(cart) {
    return cart.items.find(function (item) {
      return item.variant_id === GIFT_VARIANT_ID;
    });
  }

  function addGiftToCart() {
    log('adding gift');
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: GIFT_QUANTITY,
            properties: { _auto_gift: 'true' }
          }
        ]
      })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (err) {
          throw new Error('Add gift failed: ' + (err && err.description ? err.description : r.status));
        });
      }
      return r.json();
    });
  }

  function setGiftQuantity(lineItemKey, qty) {
    log('adjusting gift quantity to', qty);
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: qty })
    }).then(function (r) {
      if (!r.ok) throw new Error('Change gift quantity failed: ' + r.status);
      return r.json();
    });
  }

  function removeGiftFromCart(lineItemKey) {
    return setGiftQuantity(lineItemKey, 0);
  }

  function broadcastCartUpdate(cart) {
    ['cart:updated', 'cart:refresh', 'ajaxCart:updated', 'cart:build'].forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { detail: { cart: cart } }));
    });
    return cart;
  }

  // ---- Core sync logic ----------------------------------------------------
  function sync() {
    if (isSyncing) {
      pendingSync = true;
      return;
    }

    isSyncing = true;

    fetchCart()
      .then(function (cart) {
        var hasTrigger = cartHasTrigger(cart);
        var giftItem = findGiftLineItem(cart);

        if (hasTrigger && !giftItem) {
          log('trigger present, gift missing -> adding');
          return addGiftToCart().then(fetchCart).then(broadcastCartUpdate);
        }

        if (hasTrigger && giftItem && giftItem.quantity !== GIFT_QUANTITY) {
          return setGiftQuantity(giftItem.key, GIFT_QUANTITY).then(fetchCart).then(broadcastCartUpdate);
        }

        if (!hasTrigger && giftItem) {
          log('trigger missing, gift present -> removing');
          return removeGiftFromCart(giftItem.key).then(fetchCart).then(broadcastCartUpdate);
        }

        log('cart already in sync', { hasTrigger: hasTrigger, hasGift: !!giftItem });
        return null;
      })
      .catch(function (err) {
        log('sync error', err);
      })
      .then(function () {
        isSyncing = false;

        if (pendingSync) {
          pendingSync = false;
          scheduleSync(0);
        }
      });
  }

  function scheduleSync(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, delay === undefined ? 250 : delay);
  }

  // ---- Hook 1: common theme cart events -----------------------------------
  var THEME_CART_EVENTS = [
    'cart:updated',
    'cart:refresh',
    'cart:build',
    'cart:open',
    'cart:added',
    'cart:reload',
    'cart:change',
    'ajaxCart:updated',
    'ajaxCart:added',
    'ajaxProduct:added',
    'CartDrawer:updated',
    'CartDrawer:open',
    'theme:cart:change'
  ];

  THEME_CART_EVENTS.forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      scheduleSync();
    });
  });

  if (window.PubSub && typeof window.PubSub.subscribe === 'function') {
    ['cart.requestComplete', 'cart.itemAdded', 'cart.itemRemoved', 'cart.changed'].forEach(function (topic) {
      window.PubSub.subscribe(topic, function () {
        scheduleSync();
      });
    });
  }

  // ---- Hook 2: intercept fetch() calls to cart endpoints -------------------
  var CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var requestUrl = args[0] instanceof Request ? args[0].url : args[0];
    var isCartWrite = typeof requestUrl === 'string' && CART_WRITE_RE.test(requestUrl);

    return nativeFetch.apply(this, args).then(function (response) {
      if (isCartWrite) {
        scheduleSync();
      }

      return response;
    });
  };

  // ---- Hook 3: intercept XMLHttpRequest calls to cart endpoints ------------
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._autoGiftIsCartWrite = typeof url === 'string' && CART_WRITE_RE.test(url);
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this._autoGiftIsCartWrite) {
      this.addEventListener('loadend', function () {
        scheduleSync();
      });
    }

    return nativeSend.apply(this, arguments);
  };

  // ---- Hook 4: native cart page form submissions ---------------------------
  document.addEventListener('submit', function (e) {
    var form = e.target;

    if (form && form.action && /\/cart(\?|$)/.test(form.action)) {
      scheduleSync(800);
    }
  });

  // ---- Initial sync + safety-net polling ----------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleSync(0);
    });
  } else {
    scheduleSync(0);
  }

  setInterval(function () {
    scheduleSync(0);
  }, 15000);
})();